import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'

export function TermsPage() {
  return (
    <div className="min-h-dvh bg-[#FAFAFA]">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-white/80 backdrop-blur-sm border-b border-gray-100">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link
            to="/"
            className="text-[20px]"
            style={{ fontFamily: "'Oleo Script', cursive" }}
          >
            <span className="text-[#688192]">K-Beauty</span>
            <span className="text-[#46C0C6]"> AI</span>
          </Link>
          <Link
            to="/"
            className="text-sm text-[#688192] hover:text-[#46C0C6] transition-colors"
          >
            Back to Home
          </Link>
        </div>
      </header>

      {/* Content */}
      <motion.main
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-3xl mx-auto px-4 py-8"
      >
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Terms of Service</h1>
        <p className="text-sm text-gray-500 mb-8">
          Effective Date: January 12, 2026 | Last Updated: January 23, 2026
        </p>

        <div className="prose prose-gray max-w-none">
          <p className="text-gray-600 mb-6">
            Welcome to K-Skin AI ("Service"), operated by Aday Inc. ("Company," "we," "us," or "our").
            By accessing or using our Service at k-skin.ai, you agree to be bound by these Terms of Service.
          </p>

          <Section title="1. Acceptance of Terms">
            <p>
              By creating an account, making a payment, or using any part of our Service, you acknowledge
              that you have read, understood, and agree to be bound by these Terms. If you do not agree
              to these Terms, do not use our Service.
            </p>
          </Section>

          <Section title="2. Description of Service">
            <p>K-Skin AI is an AI-powered skin analysis and K-beauty product recommendation service. Our Service:</p>
            <ul>
              <li>Analyzes your uploaded selfie photo using AI technology</li>
              <li>Provides cosmetic skin metrics (Skin Age, Skin Score, texture, pores, etc.)</li>
              <li>Generates personalized AM/PM K-beauty skincare routines</li>
              <li>Delivers a downloadable PDF report with recommendations</li>
            </ul>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 my-4">
              <p className="text-amber-800 font-medium mb-1">Important Disclaimer</p>
              <p className="text-amber-700 text-sm">
                K-Skin AI provides cosmetic guidance only. This is not a medical diagnosis, treatment,
                or cure for any skin disease or medical condition. For skin diseases, medical concerns,
                or health-related issues, please consult a licensed dermatologist or healthcare provider.
              </p>
            </div>
          </Section>

          <Section title="3. Eligibility">
            <p>To use our Service, you must:</p>
            <ul>
              <li>Be at least <strong>16 years of age</strong></li>
              <li>Be a resident of or located in the <strong>United States</strong></li>
              <li>Have the legal capacity to enter into these Terms</li>
              <li>Own the rights to any photos you upload (i.e., they must be photos of yourself)</li>
            </ul>
          </Section>

          <Section title="4. Account Registration">
            <p>
              To access the full features of our Service, you must create an account using Google or Apple Sign-In.
              You agree to provide accurate and complete information, maintain the security of your account credentials,
              notify us immediately of any unauthorized access, and accept responsibility for all activities under your account.
            </p>
          </Section>

          <Section title="5. Payment and Pricing">
            <p>
              Our Service is offered at a one-time fee of <strong>$99 USD</strong> for a complete AI skin analysis
              and personalized report. Payments are processed securely through Stripe. We do not store your credit
              card information on our servers.
            </p>
            <p className="mt-2"><strong>What's Included:</strong></p>
            <ul>
              <li>Complete AI skin analysis (14 metrics)</li>
              <li>Personalized AM/PM K-beauty routine recommendations</li>
              <li>Downloadable PDF report</li>
              <li>Ingredient guidance and lifestyle tips</li>
            </ul>
          </Section>

          <Section title="6. Refund Policy">
            <p><strong>Refunds Are Available For:</strong></p>
            <ul>
              <li>Technical failures that prevent delivery of your analysis or PDF</li>
              <li>System errors that result in incomplete or corrupted reports</li>
              <li>Other technical issues evaluated on a case-by-case basis</li>
            </ul>
            <p className="mt-2"><strong>Refunds Are NOT Available For:</strong></p>
            <ul>
              <li>Successfully delivered AI analysis and PDF reports</li>
              <li>Dissatisfaction with analysis results or recommendations</li>
              <li>Change of mind after purchase</li>
              <li>User error in photo submission</li>
            </ul>
            <p className="mt-2">
              To request a refund, contact us at <strong>support@k-skin.ai</strong> within 14 days of purchase.
            </p>
          </Section>

          <Section title="7. User Conduct">
            <p>You agree NOT to:</p>
            <ul>
              <li>Upload photos of anyone other than yourself</li>
              <li>Use the Service for any illegal purpose</li>
              <li>Attempt to reverse-engineer our AI algorithms</li>
              <li>Scrape, copy, or redistribute our content</li>
              <li>Share your account credentials with others</li>
              <li>Use automated tools to access our Service</li>
            </ul>
          </Section>

          <Section title="8. Photo Upload and Usage">
            <p>By uploading a photo, you:</p>
            <ul>
              <li>Confirm you are the person in the photo</li>
              <li>Grant us permission to process the photo for AI analysis</li>
              <li>Understand the photo will be stored securely in private cloud storage</li>
              <li>Can request deletion of your photo at any time</li>
            </ul>
            <p className="mt-2">
              <strong>Photo Retention:</strong> Free teaser users' photos are automatically deleted after the teaser analysis.
              Paid users' photos are retained until deletion is requested.
            </p>
          </Section>

          <Section title="9. Product Recommendations and Affiliate Disclosure">
            <p>
              Our Service recommends K-beauty products based on your skin analysis. Some recommendations include affiliate links.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 my-4">
              <p className="text-blue-800 text-sm">
                <strong>Affiliate Disclosure:</strong> K-Skin AI may earn a commission when you purchase products through our
                affiliate links. This does not affect your price or our recommendation integrity. We recommend products based
                on your skin analysis, not commission rates.
              </p>
            </div>
          </Section>

          <Section title="10. Limitation of Liability">
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW: Our Service is provided "AS IS" without warranties of any kind.
              We do not guarantee specific results from our analysis or recommendations. We are not liable for any indirect,
              incidental, or consequential damages. Our total liability is limited to the amount you paid for the Service.
            </p>
          </Section>

          <Section title="11. Account Termination">
            <p>
              You may delete your account at any time through the app settings. Account deletion will permanently remove
              your profile information, all analysis results, all uploaded photos, and all generated PDF reports.
              This action is <strong>irreversible</strong>.
            </p>
          </Section>

          <Section title="12. Governing Law">
            <p>
              These Terms are governed by the laws of the State of California, United States, without regard to conflict
              of law principles. Any disputes shall be resolved in the courts of California.
            </p>
          </Section>

          <Section title="13. Contact Information">
            <p>For questions about these Terms, contact us at:</p>
            <p className="mt-2">
              <strong>Aday Inc.</strong><br />
              Email: support@k-skin.ai<br />
              Website: https://k-skin.ai
            </p>
          </Section>

          <div className="mt-8 pt-6 border-t border-gray-200">
            <p className="text-sm text-gray-500 italic">
              By using K-Skin AI, you acknowledge that you have read, understood, and agree to these Terms of Service.
            </p>
          </div>
        </div>
      </motion.main>
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-8">
      <h2 className="text-xl font-semibold text-gray-900 mb-3">{title}</h2>
      <div className="text-gray-600 space-y-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-1 [&_li]:text-gray-600">
        {children}
      </div>
    </div>
  )
}
